import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FLEET, SERVICES, api, estimateFare, fmtMoney, COMFORT_PACKAGE_PRICE } from "../lib/api";
import { Plane, MapPin, Clock, Sparkles, Navigation, Camera, Crown, Check, ArrowLeft, ArrowRight, CreditCard, Users, Briefcase } from "lucide-react";
import { toast } from "sonner";

const ICONS = { Plane, MapPin, Clock, Sparkles, Navigation, Camera, Crown };
const STEPS = ["Service", "Trip Details", "Vehicle Class", "Passenger", "Payment"];

export default function Booking() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    service_type: sp.get("service") || "airport-transfer",
    pickup_location: "", destination: "", pickup_date: "", pickup_time: "",
    passengers: 1, luggage: 1, hours: 2,
    flight_number: "", airline: "", airport_direction: "from-airport", flight_type: "domestic",
    additional_stops: 0, meet_greet: false,
    notes: "",
    vehicle_class_id: sp.get("vehicle") || "executive-sedan",
    referred_by: "",
    comfort_package: false,
    customer_name: sp.get("rider_name") || "", customer_email: sp.get("rider_email") || "", customer_phone: sp.get("rider_phone") || "",
    company_name: "", billing_address: "",
  });

  const vehicle = useMemo(() => FLEET.find((v) => v.id === data.vehicle_class_id), [data.vehicle_class_id]);
  const [pricingCfg, setPricingCfg] = useState(null);
  useEffect(() => { api.get("/config/pricing").then((r) => setPricingCfg(r.data)).catch(() => {}); }, []);
  const taxRate = pricingCfg?.sales_tax_rate ?? 0.08875;
  const referralPct = (pricingCfg?.referral_percent ?? 10) / 100;

  const airportCode = useMemo(() => {
    if (data.service_type !== "airport-transfer") return null;
    const hay = `${data.pickup_location} ${data.destination}`.toLowerCase();
    if (hay.includes("jfk")) return "JFK";
    if (hay.includes("ewr") || hay.includes("newark")) return "EWR";
    if (hay.includes("lga") || hay.includes("laguardia")) return "LGA";
    return null;
  }, [data.service_type, data.pickup_location, data.destination]);

  const baseFare = useMemo(() => {
    if (airportCode && pricingCfg?.airport_fares?.[airportCode] != null) return Number(pricingCfg.airport_fares[airportCode]);
    if (data.service_type === "hourly") {
      const cat = { "business-sedan": "sedan", "luxury-suv": "suv", "first-class": "van", "signature-xl": "van", "limo": "van" }[data.vehicle_class_id] || "sedan";
      const hr = pricingCfg?.hourly_rates?.[cat];
      if (hr) return Math.max(2, Number(data.hours) || 2) * Number(hr);
    }
    return estimateFare({ service_type: data.service_type, hours: data.hours, vehicle });
  }, [airportCode, pricingCfg, data.service_type, data.hours, data.vehicle_class_id, vehicle]);

  const comfortFee = 0; // Comfort Package is complimentary for all packages
  const stopFee = pricingCfg?.stop_fee ?? 10;
  const stopsCount = Math.max(0, Number(data.additional_stops) || 0);
  const stopsCharge = stopsCount * stopFee;
  const mgFee = data.meet_greet ? (pricingCfg?.meet_greet_fee ?? 15) : 0;
  const [creditCount, setCreditCount] = useState(0);
  const [applyCredit, setApplyCredit] = useState(false);
  const creditsUsed = applyCredit ? creditCount : 0;
  const referralDiscount = Math.round(Math.min(creditsUsed * referralPct, 1) * baseFare * 100) / 100;
  const salesTax = Math.round(Math.max(baseFare - referralDiscount + comfortFee + stopsCharge + mgFee, 0) * taxRate * 100) / 100;
  const fare = Math.round((Math.max(baseFare - referralDiscount + comfortFee + stopsCharge + mgFee, 0) + salesTax) * 100) / 100;

  const routeError = useMemo(() => {
    const p = (data.pickup_location || "").toLowerCase();
    const d = (data.destination || "").toLowerCase();
    const isWest = (t) => t.includes("westchester") || t.includes("hpn");
    const isNJ = (t) => t.includes("new jersey") || t.includes("newark") || t.includes("ewr") || /(,|\s)nj\b/.test(t);
    const isNYC = (t) => ["nyc","new york","manhattan","brooklyn","queens","bronx","staten","jfk","lga","midtown","soho","tribeca","harlem","chelsea","uptown","downtown","wall st"].some(k => t.includes(k));
    if (isWest(p) || isWest(d)) {
      const other = isWest(p) ? d : p;
      if ((isWest(p) && isWest(d)) || !isNYC(other)) return "Westchester Airport service is available between Westchester Airport and NYC only.";
    }
    if (isNJ(p) || isNJ(d)) {
      const other = isNJ(p) ? d : p;
      if ((isNJ(p) && isNJ(d)) || !isNYC(other)) return "New Jersey service is available between New Jersey and NYC only.";
    }
    return null;
  }, [data.pickup_location, data.destination]);

  useEffect(() => {
    const em = (data.customer_email || "").trim();
    if (!em.includes("@") || !em.includes(".")) { setCreditCount(0); return; }
    const t = setTimeout(async () => {
      try {
        const { data: lk } = await api.get("/referrals/lookup", { params: { email: em } });
        setCreditCount(lk.available_credits || 0);
      } catch { setCreditCount(0); }
    }, 400);
    return () => clearTimeout(t);
  }, [data.customer_email]);
  const isAirport = data.service_type === "airport-transfer";
  const isHourly = data.service_type === "hourly";
  const isEvent = data.service_type === "special-event";
  const isLD = data.service_type === "long-distance";
  const isPhoto = data.service_type === "photoshoot";
  const isLimo = data.service_type === "limo";

  useEffect(() => {
    if (isEvent) navigate("/contact?subject=event");
    if (isLD) navigate("/contact?subject=quote");
    if (isPhoto) navigate("/contact?subject=photoshoot");
    if (isLimo) navigate("/contact?subject=quote&vehicle=limo");
  }, [isEvent, isLD, isPhoto, isLimo, navigate]);

  const canNext = () => {
    if (step === 1) return !!data.service_type;
    if (step === 2) return data.pickup_location && data.pickup_date && data.pickup_time && (isHourly || data.destination) && !routeError;
    if (step === 3) return !!data.vehicle_class_id;
    if (step === 4) return data.customer_name && data.customer_email && data.customer_phone;
    return true;
  };

  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (submitting) return; // idempotency: prevent duplicate reservations on double-click
    setSubmitting(true);
    try {
      const payload = { ...data, vehicle_class_name: vehicle.name, estimated_fare: fare, referral_credits_used: creditsUsed };
      const corpFlag = new URLSearchParams(window.location.search).get("corporate");
      if (corpFlag && localStorage.getItem("lume_corp_token")) {
        const meResp = await api.get("/corporate/me");
        payload.company_id = meResp.data.id;
        payload.company_name = meResp.data.company_name;
      }
      const { data: reservation } = await api.post("/reservations", payload);
      const origin_url = window.location.origin;
      try {
        const { data: co } = await api.post("/payments/checkout", { reservation_number: reservation.reservation_number, amount: fare, origin_url });
        window.location.href = co.checkout_url;
      } catch (e) {
        toast.error("Payment redirect failed. Your reservation is saved — we'll follow up shortly.");
        navigate(`/confirmation/${reservation.reservation_number}`);
      }
    } catch (e) {
      toast.error("Could not create reservation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="section-container py-12 sm:py-20">
      <div className="max-w-2xl">
        <div className="eyebrow mb-3">Reserve Your Chauffeur</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-white leading-tight">Book a Ride</h1>
      </div>

      {/* Stepper */}
      <div className="mt-10 flex items-center gap-2 sm:gap-4 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 sm:gap-4 flex-shrink-0" data-testid={`step-indicator-${i+1}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono border ${i+1 === step ? "bg-[color:var(--lume-gold)] text-black border-[color:var(--lume-gold)]" : i+1 < step ? "bg-[color:var(--lume-gold)]/20 text-[color:var(--lume-gold)] border-[color:var(--lume-gold)]/40" : "text-slate-500 border-white/10"}`}>
              {i+1 < step ? <Check size={14}/> : i+1}
            </div>
            <div className={`text-xs uppercase tracking-widest ${i+1 === step ? "text-white" : "text-slate-500"}`}>{s}</div>
            {i < STEPS.length - 1 && <div className="w-6 sm:w-10 h-px bg-white/10"/>}
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {step === 1 && (
            <div className="lume-card p-6 sm:p-8 fade-up">
              <h2 className="font-serif text-2xl text-white mb-6">Choose your service</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SERVICES.map((s) => {
                  const Icon = ICONS[s.icon];
                  const active = data.service_type === s.id;
                  return (
                    <button key={s.id} onClick={() => setData({...data, service_type: s.id})} data-testid={`booking-service-${s.id}`}
                      className={`p-5 rounded-xl border text-left transition-all ${active ? "border-[color:var(--lume-gold)] bg-[color:var(--lume-gold)]/5" : "border-white/10 hover:border-white/20"}`}>
                      <Icon className={active ? "text-[color:var(--lume-gold)]" : "text-slate-400"} size={20}/>
                      <div className="font-serif text-white text-lg mt-3">{s.title}</div>
                      <div className="text-xs text-slate-400 mt-2 leading-relaxed">{s.subtitle}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="lume-card p-6 sm:p-8 fade-up">
              <h2 className="font-serif text-2xl text-white mb-6">Trip details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isAirport && (
                  <div className="sm:col-span-2">
                    <label className="field-label">Direction</label>
                    <div className="flex gap-3">
                      {[{k:"from-airport", l:"Pickup FROM Airport"}, {k:"to-airport", l:"Drop-off TO Airport"}].map((o) => (
                        <button key={o.k} onClick={() => setData({...data, airport_direction: o.k})} data-testid={`airport-${o.k}`}
                          className={`flex-1 py-2.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${data.airport_direction === o.k ? "border-[color:var(--lume-gold)] text-[color:var(--lume-gold)]" : "border-white/10 text-slate-400"}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div><label className="field-label">Pickup Location</label><input className="input-lume" value={data.pickup_location} onChange={(e)=>setData({...data, pickup_location:e.target.value})} placeholder="Address, hotel, terminal…" data-testid="booking-pickup"/></div>
                <div><label className="field-label">{isHourly ? "Direction / Notes" : "Destination"}</label><input className="input-lume" value={data.destination} onChange={(e)=>setData({...data, destination:e.target.value})} placeholder={isHourly ? "As directed" : "Address, airport…"} data-testid="booking-destination"/></div>
                <div><label className="field-label">Date</label><input type="date" className="input-lume" value={data.pickup_date} onChange={(e)=>setData({...data, pickup_date:e.target.value})} data-testid="booking-date"/></div>
                <div><label className="field-label">Pickup Time</label><input type="time" className="input-lume" value={data.pickup_time} onChange={(e)=>setData({...data, pickup_time:e.target.value})} data-testid="booking-time"/></div>
                <div><label className="field-label">Passengers</label><input type="number" min={1} className="input-lume" value={data.passengers} onChange={(e)=>setData({...data, passengers:+e.target.value})} data-testid="booking-passengers"/></div>
                <div><label className="field-label">Luggage</label><input type="number" min={0} className="input-lume" value={data.luggage} onChange={(e)=>setData({...data, luggage:+e.target.value})} data-testid="booking-luggage"/></div>
                {isAirport && (
                  <div className="sm:col-span-2">
                    <label className="field-label">Flight Type</label>
                    <div className="flex gap-3">
                      {[{k:"domestic", l:"Domestic — 30 min free wait"}, {k:"international", l:"International — 45 min free wait"}].map((o) => (
                        <button key={o.k} type="button" onClick={() => setData({...data, flight_type: o.k})} data-testid={`flight-${o.k}`}
                          className={`flex-1 py-2.5 rounded-full text-xs uppercase tracking-widest border transition-colors ${data.flight_type === o.k ? "border-[color:var(--lume-gold)] text-[color:var(--lume-gold)]" : "border-white/10 text-slate-400"}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {isHourly && <div><label className="field-label">Hours (min 2)</label><input type="number" min={2} className="input-lume" value={data.hours} onChange={(e)=>setData({...data, hours:+e.target.value})} data-testid="booking-hours"/></div>}
                {isAirport && <>
                  <div><label className="field-label">Airline</label><input className="input-lume" value={data.airline} onChange={(e)=>setData({...data, airline:e.target.value})} data-testid="booking-airline"/></div>
                  <div><label className="field-label">Flight Number</label><input className="input-lume" value={data.flight_number} onChange={(e)=>setData({...data, flight_number:e.target.value})} data-testid="booking-flight"/></div>
                </>}
                <div className="sm:col-span-2"><label className="field-label">Referred By (optional)</label><input className="input-lume" value={data.referred_by} onChange={(e)=>setData({...data, referred_by:e.target.value})} placeholder="Name of the person who referred you" data-testid="booking-referred-by"/></div>
                <div><label className="field-label">Additional Stops (fees may apply)</label><input type="number" min={0} className="input-lume" value={data.additional_stops} onChange={(e)=>setData({...data, additional_stops:+e.target.value})} data-testid="booking-stops"/></div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer w-full" style={{ borderColor: data.meet_greet ? "var(--lume-gold)" : "rgba(255,255,255,0.08)" }} data-testid="meet-greet-toggle">
                    <input type="checkbox" checked={data.meet_greet} onChange={(e)=>setData({...data, meet_greet:e.target.checked})} className="accent-[color:var(--lume-gold)]" data-testid="meet-greet-checkbox"/>
                    <span className="text-sm text-white">Meet-and-Greet <span className="text-xs text-[color:var(--lume-gold)]">— $15</span></span>
                  </label>
                </div>
                {routeError && <div className="sm:col-span-2 text-sm text-amber-300 border border-amber-500/30 rounded-lg p-3" data-testid="route-error">{routeError}</div>}
                <div className="sm:col-span-2"><label className="field-label">Additional Notes</label><textarea rows={3} className="input-lume" value={data.notes} onChange={(e)=>setData({...data, notes:e.target.value})} placeholder="Specific vehicle request, child seat, meet & greet…" data-testid="booking-notes"/></div>
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors" style={{ borderColor: data.comfort_package ? "var(--lume-gold)" : "rgba(255,255,255,0.08)", background: data.comfort_package ? "rgba(212,175,55,0.05)" : "transparent" }} data-testid="comfort-package-toggle">
                    <input type="checkbox" checked={data.comfort_package} onChange={(e)=>setData({...data, comfort_package:e.target.checked})} className="mt-1 accent-[color:var(--lume-gold)]" data-testid="comfort-package-checkbox"/>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-serif text-white">Comfort Package <span className="text-xs uppercase tracking-widest text-[color:var(--lume-gold)] ml-2">Optional Add-On</span></div>
                          <div className="text-xs text-slate-400 mt-1">Bottled water — Complimentary for you and your guests.</div>
                        </div>
                        <div className="font-mono text-sm text-emerald-400">Complimentary</div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="lume-card p-6 sm:p-8 fade-up">
              <h2 className="font-serif text-2xl text-white mb-2">Select vehicle class</h2>
              <p className="text-sm text-slate-400 mb-6">Specific vehicle requests may be accommodated when available. Please add your request in the additional notes section.</p>
              <div className="space-y-4">
                {FLEET.filter(v => !v.quote_only).map((v) => {
                  const active = data.vehicle_class_id === v.id;
                  return (
                    <button key={v.id} onClick={() => setData({...data, vehicle_class_id: v.id})} data-testid={`booking-vehicle-${v.id}`}
                      className={`w-full text-left rounded-xl border overflow-hidden transition-all flex ${active ? "border-[color:var(--lume-gold)] bg-[color:var(--lume-gold)]/5" : "border-white/10 hover:border-white/20"}`}>
                      <img src={v.image} alt={v.name} className="w-32 sm:w-52 h-32 sm:h-40 object-cover flex-shrink-0"/>
                      <div className="p-4 sm:p-5 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-serif text-lg text-white">{v.name}</div>
                            <div className="text-xs text-slate-400 mt-1 italic">{v.tagline}</div>
                          </div>
                          {active && <Check className="text-[color:var(--lume-gold)]" size={18}/>}
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-300">
                          <span className="flex items-center gap-1"><Users size={12} className="text-[color:var(--lume-gold)]"/>{v.capacity}</span>
                          <span className="flex items-center gap-1"><Briefcase size={12} className="text-[color:var(--lume-gold)]"/>{v.luggage}</span>
                        </div>
                        {v.extra_note && <p className="mt-2 text-xs text-amber-300/80">{v.extra_note}</p>}
                      </div>
                    </button>
                  );
                })}
                <div className="lume-card p-5 border border-[color:var(--lume-gold)]/30">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-serif text-lg text-white">Signature XL Service</div>
                      <div className="text-xs text-slate-400 mt-1">12–15 passengers · Special pricing available. Request a quote.</div>
                    </div>
                    <button onClick={() => navigate('/contact?subject=quote&vehicle=signature-xl')} className="btn-outline-gold !py-2 !px-4 !text-xs" data-testid="booking-signature-xl-quote">Request a Quote</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="lume-card p-6 sm:p-8 fade-up">
              <h2 className="font-serif text-2xl text-white mb-6">Your information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="field-label">Full Name</label><input className="input-lume" value={data.customer_name} onChange={(e)=>setData({...data, customer_name:e.target.value})} data-testid="booking-cust-name"/></div>
                <div><label className="field-label">Email</label><input type="email" className="input-lume" value={data.customer_email} onChange={(e)=>setData({...data, customer_email:e.target.value})} data-testid="booking-cust-email"/></div>
                <div><label className="field-label">Phone</label><input className="input-lume" value={data.customer_phone} onChange={(e)=>setData({...data, customer_phone:e.target.value})} data-testid="booking-cust-phone"/></div>
                <div><label className="field-label">Company (optional)</label><input className="input-lume" value={data.company_name} onChange={(e)=>setData({...data, company_name:e.target.value})} data-testid="booking-cust-company"/></div>
                <div className="sm:col-span-2"><label className="field-label">Billing Address (optional)</label><input className="input-lume" value={data.billing_address} onChange={(e)=>setData({...data, billing_address:e.target.value})} data-testid="booking-billing"/></div>
                {creditCount > 0 && (
                  <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors" style={{ borderColor: applyCredit ? "var(--lume-gold)" : "rgba(255,255,255,0.08)", background: applyCredit ? "rgba(212,175,55,0.05)" : "transparent" }} data-testid="apply-referral-credit">
                      <input type="checkbox" checked={applyCredit} onChange={(e)=>setApplyCredit(e.target.checked)} className="mt-1 accent-[color:var(--lume-gold)]" data-testid="apply-credit-checkbox"/>
                      <div className="flex-1 flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-serif text-white">Referral Discount Available</div>
                          <div className="text-xs text-slate-400 mt-1">You have {creditCount} successful referral{creditCount > 1 ? "s" : ""} — {Math.round(referralPct*100)}% off each — on {data.customer_email}.</div>
                        </div>
                        <div className="font-mono text-sm text-emerald-400">− {fmtMoney(Math.round(Math.min(creditCount * referralPct, 1) * baseFare * 100) / 100)}</div>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="lume-card p-6 sm:p-8 fade-up">
              <h2 className="font-serif text-2xl text-white mb-2">Secure your payment method</h2>
              <p className="text-sm text-slate-400 mb-6">You'll be redirected to our secure Stripe checkout. We never store raw card data. Final fare is calculated after your ride is complete.</p>
              <div className="rounded-xl border border-white/10 bg-black/40 p-5">
                <div className="flex items-center gap-3 text-slate-300 mb-4">
                  <CreditCard className="text-[color:var(--lume-gold)]" size={18}/>
                  <div>
                    <div className="font-serif text-white">Pay with Card</div>
                    <div className="text-xs text-slate-400 mt-1">Stripe Secure Checkout</div>
                  </div>
                </div>
                <button onClick={() => submit()} disabled={submitting} className="btn-gold w-full" data-testid="booking-confirm-btn">{submitting ? "Processing…" : "Confirm & Pay"}</button>
              </div>
              <p className="mt-5 text-xs text-slate-500 leading-relaxed">By confirming, you authorize Lume Enterprise to charge the payment method upon ride completion. An invoice and receipt will be emailed after the ride.</p>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <button onClick={() => setStep(Math.max(1, step-1))} disabled={step===1} className="btn-ghost disabled:opacity-30" data-testid="booking-back-btn">
              <ArrowLeft size={14} className="inline mr-2"/>Back
            </button>
            {step < 5
              ? <button onClick={() => canNext() ? setStep(step+1) : toast.error("Please complete this step.")} className="btn-gold" data-testid="booking-next-btn">Continue <ArrowRight size={14} className="inline ml-2"/></button>
              : null
            }
          </div>
        </div>

        {/* Summary */}
        <aside className="lume-card p-6 h-fit lg:sticky lg:top-24">
          <div className="eyebrow mb-3">Reservation Summary</div>
          <table className="w-full text-sm">
            <tbody className="[&_td]:py-1.5">
              <tr><td className="text-slate-400">Service</td><td className="text-white text-right">{SERVICES.find(s=>s.id===data.service_type)?.title || "—"}</td></tr>
              <tr><td className="text-slate-400">Vehicle</td><td className="text-white text-right">{vehicle?.name}</td></tr>
              <tr><td className="text-slate-400">Date</td><td className="text-white text-right">{data.pickup_date || "—"}</td></tr>
              <tr><td className="text-slate-400">Time</td><td className="text-white text-right">{data.pickup_time || "—"}</td></tr>
              <tr><td className="text-slate-400">Passengers</td><td className="text-white text-right">{data.passengers}</td></tr>
              {isHourly && <tr><td className="text-slate-400">Hours</td><td className="text-white text-right">{data.hours}</td></tr>}
              <tr><td className="text-slate-400">Base Fare{airportCode ? ` (${airportCode} ↔ NYC)` : ""}</td><td className="text-white text-right">{fmtMoney(baseFare)}</td></tr>
              {data.comfort_package && <tr><td className="text-slate-400">Comfort Package</td><td className="text-emerald-400 text-right">Complimentary</td></tr>}
              {stopsCharge > 0 && <tr><td className="text-slate-400">Additional Stops ({stopsCount} × ${stopFee})</td><td className="text-white text-right">+ {fmtMoney(stopsCharge)}</td></tr>}
              {mgFee > 0 && <tr><td className="text-slate-400">Meet-and-Greet</td><td className="text-white text-right">+ {fmtMoney(mgFee)}</td></tr>}
              {referralDiscount > 0 && <tr><td className="text-slate-400">Referral Discount ({creditsUsed} × {Math.round(referralPct*100)}%)</td><td className="text-emerald-400 text-right">− {fmtMoney(referralDiscount)}</td></tr>}
              <tr><td className="text-slate-400">Sales Tax ({(taxRate*100).toFixed(3)}%)</td><td className="text-white text-right">+ {fmtMoney(salesTax)}</td></tr>
              <tr><td className="text-slate-400">Tolls</td><td className="text-slate-400 text-right text-xs">Separate</td></tr>
            </tbody>
          </table>
          <div className="gold-hairline my-4"/>
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-slate-400">Total Before Tolls</div>
            <div className="font-serif text-2xl text-[color:var(--lume-gold-light)]" data-testid="booking-fare">{fmtMoney(fare)}</div>
          </div>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">Tolls are separate and may apply depending on the route. Final fare confirmed after ride completion.</p>
          <div className="gold-hairline my-4"/>
          <div className="space-y-2 text-xs text-slate-500 leading-relaxed" data-testid="policy-notes">
            <div><span className="text-slate-400">Airport Transfers:</span> 30 min complimentary wait (domestic) · 45 min (international).</div>
            {!isAirport && <div><span className="text-slate-400">Reservations:</span> 15-minute grace period; after that fees may apply</div>}
            <div><span className="text-slate-400">Additional Stops:</span> Fees may apply. <span className="text-slate-400">Meet-and-Greet:</span> $15.</div>
            <div><span className="text-slate-400">Special Pricing:</span> Photoshoot (2-hour minimum), Limo & Signature XL — quote required.</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
